import { ReactNode, useLayoutEffect, useMemo, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { useEnsurePromptVersionLogsSearchContext } from "./hooks/useEnsurePromptVersionLogsSearchContext";
import { usePromptVersionLogsDateRangeConfig } from "./hooks/usePromptVersionLogsDateRangeConfig";
import { buildPromptVersionLockedFilters } from "./utils/buildPromptVersionLockedFilters";

import { StructuredSearchProvider, useStructuredSearchStore } from "@/components/StructuredSearch";
import type { Filter } from "@/components/StructuredSearch/config/types";
import {
  PROMPT_ANALYTICS_URL_NAMESPACE,
  readNamespacedParam,
} from "@/components/StructuredSearch/hooks/structuredSearchUrlNamespaces";
import { useSyncStructuredSearchConstraints } from "@/components/StructuredSearch/hooks/useSyncStructuredSearchConstraints";
import {
  createStructuredSearchDefaultDateRange,
  type CompleteDateRange,
} from "@/components/StructuredSearch/stores/structured-search/structured-search-store";
import { useAuth } from "@/context/auth-context";
import { usePromptRegistry, usePromptVersion } from "@/queries";

interface PromptVersionLogsSearchProviderProps {
  children: ReactNode;
  /** Skip logs-specific queries when Analytics is unavailable (e.g. snippets). */
  enabled?: boolean;
}

const isDefaultStructuredSearchDateRange = (from: Date, to: Date): boolean => {
  const defaultRange = createStructuredSearchDefaultDateRange();
  return (
    Math.abs(from.getTime() - defaultRange.from.getTime()) < 60_000 &&
    Math.abs(to.getTime() - defaultRange.to.getTime()) < 60_000
  );
};

const PromptVersionLogsSearchSync = ({
  lockedFilters,
  dateRange,
  searchContextReady,
}: {
  lockedFilters: Filter[];
  dateRange?: CompleteDateRange;
  searchContextReady: boolean;
}) => {
  const store = useStructuredSearchStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasSeededRef = useRef(false);
  const startParam = readNamespacedParam(searchParams, PROMPT_ANALYTICS_URL_NAMESPACE, "startTime");
  const endParam = readNamespacedParam(searchParams, PROMPT_ANALYTICS_URL_NAMESPACE, "endTime");
  const startTime = startParam ? new Date(startParam) : null;
  const endTime = endParam ? new Date(endParam) : null;
  const urlHasCustomDateRange =
    !!startTime && !!endTime && !isDefaultStructuredSearchDateRange(startTime, endTime);

  // Seed version.created_at once per provider mount unless the URL already has a custom range.
  useLayoutEffect(() => {
    if (!dateRange || hasSeededRef.current || urlHasCustomDateRange) {
      return;
    }

    hasSeededRef.current = true;
    store.setDateRange(dateRange);
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set(PROMPT_ANALYTICS_URL_NAMESPACE.startTime, dateRange.from.toISOString());
        next.set(PROMPT_ANALYTICS_URL_NAMESPACE.endTime, dateRange.to.toISOString());
        return next;
      },
      { replace: true },
    );
  }, [dateRange, setSearchParams, store, urlHasCustomDateRange]);

  useLayoutEffect(() => {
    // Block URL sync / grid fetch until the version date is seeded and locked
    // prompt filters are available (avoids unfiltered workspace-wide searches).
    store.setUrlHydrationReady(
      searchContextReady &&
        lockedFilters.length > 0 &&
        (urlHasCustomDateRange || hasSeededRef.current || !dateRange),
    );
  }, [dateRange, lockedFilters.length, searchContextReady, store, urlHasCustomDateRange]);

  useSyncStructuredSearchConstraints({
    lockedFilters: lockedFilters.length > 0 ? lockedFilters : undefined,
    syncDateRange: false,
    store,
  });

  return null;
};

const PromptVersionLogsSearchProvider = ({
  children,
  enabled = true,
}: PromptVersionLogsSearchProviderProps) => {
  const { promptId, versionId } = useParams();
  const userToken = useAuth()?.userToken;
  const shouldLoadLogsContext = enabled && !!promptId && !!versionId;

  const promptRegistry = usePromptRegistry(
    userToken || "",
    shouldLoadLogsContext ? Number(promptId) : undefined,
    false,
  );
  const promptVersion = usePromptVersion(
    userToken || "",
    shouldLoadLogsContext ? Number(promptId) : null,
    shouldLoadLogsContext && versionId ? Number(versionId) : null,
  );

  const lockedPromptFilters = useMemo(
    () =>
      buildPromptVersionLockedFilters({
        promptId: shouldLoadLogsContext ? promptId : undefined,
        promptName: promptRegistry.data?.prompt_name,
        promptVersionNumber: promptVersion.data?.number,
      }),
    [promptId, promptRegistry.data?.prompt_name, promptVersion.data?.number, shouldLoadLogsContext],
  );

  const versionDateRangeConfig = usePromptVersionLogsDateRangeConfig({
    promptVersion: shouldLoadLogsContext ? promptVersion.data : undefined,
  });

  const searchContextReady = useEnsurePromptVersionLogsSearchContext(
    shouldLoadLogsContext ? promptId : undefined,
    shouldLoadLogsContext ? versionId : undefined,
  );

  const providerKey = `${promptId ?? "pending"}:${versionId ?? "pending"}`;

  return (
    <StructuredSearchProvider key={providerKey}>
      {shouldLoadLogsContext ? (
        <PromptVersionLogsSearchSync
          lockedFilters={lockedPromptFilters}
          dateRange={versionDateRangeConfig?.initialDateRange}
          searchContextReady={searchContextReady}
        />
      ) : null}
      {children}
    </StructuredSearchProvider>
  );
};

export default PromptVersionLogsSearchProvider;
